import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { Empty, Form, Input, Typography } from 'antd'
import useDirectory from 'hooks/useDirectory'
import useIntersectionObserver from 'hooks/useIntersectionObserver'
import ViewerFilePreview from 'pages/building/buildingDetailViewMode/components/ViewerFilePreview'
import PreviewFile from 'pages/building/panoramas/components/PreviewFile'
import useSWR from 'swr'
import {
  BuildingChildrenFolder,
  BuildingFile,
  Folders,
  ListBuildingFiles
} from 'types/building'
import Validators from 'utils/validators'

import Button from 'components/atoms/Button'
import Spinner from 'components/atoms/Spinner'
import ViewFile360Icon from 'components/icons/360ViewFileIcon'
import ChildrenFolderIcon from 'components/icons/ChildrenFolderIcon'

import FolderIcon from './FolderIcon'
import ModalListRelatedPoints from './ModalListRelatedPoints'

const PAGE_SIZE = 20

type Props = {
  listFolder: Folders[]
  setChildrenFolder: Dispatch<SetStateAction<string>>
}

export default function ListFile({ listFolder, setChildrenFolder }: Props) {
  const { id } = useParams()
  const { t } = useTranslation()
  const { getDirectory, getDirectoryPrimaryColor } = useDirectory()

  const [form] = Form.useForm()
  const [selectedFile, setSelectedFile] = useState<
    string | number | undefined
  >()

  // Pagination state for infinite scroll
  const [page, setPage] = useState(1)
  const [allFiles, setAllFiles] = useState<BuildingFile[]>([])

  const [searchParams, setSearchParams] = useSearchParams()
  const folder = searchParams.get('folder')
  const children = searchParams.get('children')
  const selectedChildrenFolder = searchParams.get('children_folder')
  const previewIndex = searchParams.get('preview_idx')
  const keyword = searchParams.get('keyword')
  const annotationTypeParam = searchParams.get('annotation_type')
  const categoryParam = searchParams.get('category_filter')

  const filtersKey = `${folder}-${children}-${keyword}-${annotationTypeParam}-${categoryParam}`
  const [lastFiltersKey, setLastFiltersKey] = useState(filtersKey)

  const selectedFolder = useMemo(
    () => listFolder.find((item) => `${item.id}` === folder),
    [folder, listFolder]
  )

  const selectedSubFolder = useMemo(
    () =>
      selectedFolder?.children?.find(
        (item: Folders) => `${item.categoryId}` === children
      ),
    [children, selectedFolder?.children]
  )

  const isShowingFileLevel =
    selectedFolder && (selectedFolder.isFileHolder || selectedSubFolder)

  // Fetch paginated file list
  const { data: buildingFiles, isValidating } = useSWR<ListBuildingFiles>(
    isShowingFileLevel && !selectedChildrenFolder
      ? {
          url: '/v1/buildings/files',
          params: {
            building_id: id,
            category: selectedFolder?.isFileHolder
              ? selectedFolder.categoryId
              : selectedSubFolder?.categoryId,
            keyword,
            annotation_type: annotationTypeParam || undefined,
            category_filter: categoryParam || undefined,
            page,
            page_size: PAGE_SIZE
          }
        }
      : null
  )

  // Accumulate files as pages load (infinite scroll pattern) with filter reset handling
  useEffect(() => {
    if (filtersKey !== lastFiltersKey) {
      setPage(1)
      setAllFiles([])
      setLastFiltersKey(filtersKey)
    } else if (buildingFiles?.files.files) {
      if (page === 1) {
        // First page: replace the array
        setAllFiles(
          [...buildingFiles.files.files].sort((a, b) =>
            a.name.localeCompare(b.name)
          )
        )
      } else {
        // Subsequent pages: append, avoid duplicates
        setAllFiles((prev) => {
          const existingIds = new Set(prev.map((f) => f.id))
          const newItems = buildingFiles.files.files
            .filter((f) => !existingIds.has(f.id))
            .sort((a, b) => a.name.localeCompare(b.name))
          return [...prev, ...newItems]
        })
      }
    }
  }, [buildingFiles?.files.files, page, filtersKey, lastFiltersKey])

  const sortedFolder = useMemo(
    () =>
      [...(buildingFiles?.files.folders || [])].sort(
        (a, b) => a.order - b.order
      ),
    [buildingFiles?.files.folders]
  )

  const { data: childrenFolder, isValidating: isValidatingChildrenFolder } =
    useSWR<BuildingChildrenFolder>(
      selectedChildrenFolder
        ? {
            url: `v1/buildings/${id}/folders/${selectedChildrenFolder}`,
            params: {
              keyword
            }
          }
        : null
    )

  const sortedChildrenFolderFiles = useMemo(
    () =>
      [...(childrenFolder?.files || [])].sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
    [childrenFolder?.files]
  )

  const isFileListEmpty = useMemo(() => {
    if (
      selectedChildrenFolder &&
      !isValidatingChildrenFolder &&
      !sortedChildrenFolderFiles.length
    ) {
      return true
    }
    if (
      !isValidating &&
      !selectedChildrenFolder &&
      !sortedFolder.length &&
      !allFiles.length
    ) {
      return true
    }

    return false
  }, [
    isValidating,
    isValidatingChildrenFolder,
    selectedChildrenFolder,
    sortedChildrenFolderFiles.length,
    allFiles.length,
    sortedFolder.length
  ])

  const previewFile = useMemo(() => {
    if (previewIndex) {
      return selectedChildrenFolder
        ? sortedChildrenFolderFiles[+previewIndex]
        : allFiles[+previewIndex]
    }
    return undefined
  }, [
    previewIndex,
    selectedChildrenFolder,
    sortedChildrenFolderFiles,
    allFiles
  ])

  const previewLength = useMemo(
    () =>
      selectedChildrenFolder
        ? sortedChildrenFolderFiles.length
        : allFiles.length,
    [selectedChildrenFolder, sortedChildrenFolderFiles.length, allFiles.length]
  )

  const handleSelectChildrenFolder = (folderId: number) => {
    setSearchParams((prev) => {
      prev.set('children_folder', `${folderId}`)
      return prev
    })
  }

  const handleSearch = (values: any) => {
    setSearchParams(
      (prev) => {
        prev.set('keyword', `${values.keyword}`.normalize('NFC').trim())
        return prev
      },
      { replace: true }
    )
  }

  const handleSelectPreview = (index: number) => {
    setSearchParams((prev) => {
      prev.set('preview_idx', `${index}`)
      return prev
    })
  }

  // Infinite scroll: load next page when sentinel is visible
  const handleIntersecting = useCallback(() => {
    if (
      !isValidating &&
      buildingFiles?.pagination &&
      allFiles.length < buildingFiles.pagination.total
    ) {
      setPage((prev) => prev + 1)
    }
  }, [isValidating, buildingFiles?.pagination, allFiles.length])

  const { ref: sentinelRef } = useIntersectionObserver({
    onIntersecting: handleIntersecting
  })

  useEffect(() => {
    if (selectedChildrenFolder) {
      setChildrenFolder(childrenFolder?.name || '')
    }
  }, [childrenFolder?.name, selectedChildrenFolder, setChildrenFolder])

  useEffect(() => {
    form.setFieldValue('keyword', keyword)
  }, [form, keyword])

  if (!isShowingFileLevel) return null

  return (
    <>
      {folder && !categoryParam && !annotationTypeParam && (
        <Form form={form} preserve={false} onFinish={handleSearch}>
          <div className="flex items-center justify-between rounded-[10px] bg-white pl-10 pr-5 py-5 mb-5">
            <div className="flex items-center gap-[10px]">
              <Typography className="my-auto text-[14px] leading-[21px] text-[#021120] font-bold">
                キーワード
              </Typography>
              <Form.Item
                rules={[
                  {
                    validator: Validators.searchFileValidator(t, false, true)
                  }
                ]}
                name="keyword"
                className="mb-0 thin-error-message"
              >
                <Input className="w-[180px] h-[38px] rounded-md" />
              </Form.Item>
            </div>
            <Button type="primary" htmlType="submit">
              検索
            </Button>
          </div>
        </Form>
      )}

      <div className="grid grid-rows-[auto_1fr] overflow-auto">
        {selectedFolder && (
          <div className="px-5 theme-bg-primary">
            <div className="flex gap-4 py-1 items-center mr-auto">
              <FolderIcon type={selectedFolder.id} size="small" />
              <Typography className="text-white font-medium">
                {selectedFolder?.label}
              </Typography>
            </div>
          </div>
        )}
        <div className="overflow-auto bg-white">
          {(isValidating || isValidatingChildrenFolder) && <Spinner />}
          {isFileListEmpty && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('building.fileNotFound')}
            />
          )}
          {!isValidatingChildrenFolder && selectedChildrenFolder
            ? sortedChildrenFolderFiles.map((file, index) => (
                <div
                  key={file.id}
                  className="flex items-center gap-5 justify-between min-h-[80px] p-[10px] border-0 border-b border-solid border-[#EEEFF2]"
                >
                  <div
                    className="flex items-center gap-5 cursor-pointer"
                    onClick={() => handleSelectPreview(index)}
                  >
                    <div className="flex-shrink-0">
                      <PreviewFile
                        file={file}
                        size={
                          file.extension.toLowerCase() === 'pdf'
                            ? 'default'
                            : 'large'
                        }
                      />
                    </div>
                    <Typography className="font-medium text-[#021120]">
                      {file.name}
                    </Typography>
                  </div>
                  {file.isRelated && (
                    <Button
                      className="border-[1px] border-solid theme-border-primary theme-text-primary gap-[3px] py-[7px] px-[3px] bg-white flex justify-center items-center rounded-[4px] hover:!cursor-pointer hover:!theme-text-primary hover:!bg-white hover:!opacity-100 mr-[28px]"
                      onClick={() => setSelectedFile(file.id)}
                      sizing="w-[160px] h-[40px]"
                    >
                      <ViewFile360Icon />
                      {t('building.360ViewFile')}
                    </Button>
                  )}
                </div>
              ))
            : null}

          {!isValidating && !selectedChildrenFolder ? (
            <>
              {getDirectory() !== 'bcp' &&
                sortedFolder.map((item) => (
                  <div
                    key={item.id}
                    className="min-h-[80px] flex gap-5 items-center p-[10px] border-0 border-b border-solid border-[#EEEFF2] cursor-pointer"
                    onClick={() => handleSelectChildrenFolder(item.id)}
                  >
                    <ChildrenFolderIcon />
                    <div className="text-base text-[#021120]">{item.name}</div>
                  </div>
                ))}
              {allFiles.map((file, index) => (
                <div
                  key={file.id}
                  className="flex items-center gap-5 justify-between min-h-[80px] p-[10px] border-0 border-b border-solid border-[#EEEFF2]"
                >
                  <div
                    className="flex items-center gap-5 cursor-pointer"
                    onClick={() => handleSelectPreview(index)}
                  >
                    <div className="flex-shrink-0">
                      <PreviewFile
                        file={file}
                        size={
                          file.extension.toLowerCase() === 'pdf'
                            ? 'default'
                            : 'large'
                        }
                      />
                    </div>
                    <Typography className="font-medium text-[#021120]">
                      {file.name}
                    </Typography>
                  </div>
                  {file.isRelated && (
                    <Button
                      className="related-point-button gap-[3px] py-[7px] px-[3px] mr-[28px]"
                      onClick={() => setSelectedFile(file.id)}
                      sizing="w-[160px] h-[40px]"
                    >
                      <ViewFile360Icon fill={getDirectoryPrimaryColor()} />
                      {t('building.360ViewFile')}
                    </Button>
                  )}
                </div>
              ))}

              {/* Infinite scroll sentinel */}
              <Spinner spinning={isValidating}>
                <div ref={sentinelRef} className="h-10" />
              </Spinner>
            </>
          ) : null}
        </div>
      </div>

      {buildingFiles && previewFile ? (
        <ViewerFilePreview
          fileIndex={undefined}
          setPreviewFileIndex={undefined}
          file={previewFile}
          maxLength={previewLength}
        />
      ) : null}

      <ModalListRelatedPoints
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
      />
    </>
  )
}
